import React, { useState, useEffect, useCallback, useRef } from 'react';
import { C, card, sectionTitle, filterPill, kpiCard, kpiIcon, toggleGroup, toggleBtn, th, td, tdR } from '../../styles';
import { getPaymentAnalysis, getPaymentFilterOptions, getVisualPrefs, setVisualPref } from '../../services/api';
const ReactECharts = require('echarts-for-react').default;

// ─── helpers ──────────────────────────────────────────────────────────────────

const parseLocalDate = (str) => {
  if (!str) return null;
  const s = String(str);
  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]);
  const d = new Date(s);
  if (!isNaN(d.getTime())) return new Date(d.getFullYear(), d.getMonth(), d.getDate());
  return null;
};

const fmtDate = (str, opts) => {
  const d = parseLocalDate(str);
  return d ? d.toLocaleDateString('en-GB', opts) : str;
};

const toISOKey = (str) => {
  if (!str) return '';
  const m = String(str).match(/(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  const d = new Date(str);
  if (!isNaN(d.getTime())) {
    const pad = n => String(n).padStart(2,'0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  }
  return String(str);
};

const isMultiYear = (isoKeys) => {
  if (!isoKeys.length) return false;
  return new Set(isoKeys.map(p => String(p).slice(0, 4))).size > 1;
};

const buildMarkArea = (isoKeys) => {
  if (!isoKeys.length) return [];
  const groups = {};
  isoKeys.forEach(p => {
    const yr = String(p).slice(0, 4);
    if (!groups[yr]) groups[yr] = { start: p, end: p };
    groups[yr].end = p;
  });
  return Object.entries(groups).map(([, { start, end }], i) => [
    { xAxis: start, itemStyle: { color: i % 2 === 0 ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)' }, label: { show: false } },
    { xAxis: end }
  ]);
};

const makeLabelFmt = (period, multiYear) => (v) => {
  try {
    const d = parseLocalDate(v);
    if (!d) return v;
    if (period === 'monthly') {
      const mmm = d.toLocaleDateString('en-GB', { month:'short' });
      if (multiYear && d.getMonth() === 0) return mmm + '\n' + d.getFullYear();
      return mmm;
    }
    const label = d.toLocaleDateString('en-GB', { day:'numeric', month:'short' });
    if (multiYear && d.getDate() <= 7 && d.getMonth() === 0) return label + '\n' + d.getFullYear();
    return label;
  } catch { return v; }
};

// Add this NEW function after helper functions, before constants:
const sortPeriodKeys = (periodArray) => {
  return periodArray
    .map(p => toISOKey(p))
    .sort((a, b) => {
      const dateA = parseLocalDate(a);
      const dateB = parseLocalDate(b);
      if (!dateA || !dateB) return 0;
      return dateA - dateB;
    });
};

const generateFilledPeriodKeys = (sortedISO, granularity) => {
  if (!sortedISO.length) return sortedISO;
  const start = parseLocalDate(sortedISO[0]);
  const end   = parseLocalDate(sortedISO[sortedISO.length - 1]);
  if (!start || !end) return sortedISO;
  const pad = n => String(n).padStart(2, '0');
  const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const result = [];
  const cur = new Date(start);
  while (cur <= end) {
    result.push(fmt(cur));
    if (granularity === 'monthly') cur.setMonth(cur.getMonth() + 1);
    else if (granularity === 'weekly') cur.setDate(cur.getDate() + 7);
    else cur.setDate(cur.getDate() + 1);
  }
  return result;
};

// ─── constants ────────────────────────────────────────────────────────────────

const MODE_COLORS = [C.accent, C.blue, C.amber, C.purple, '#FF6B6B', '#34D399', '#F472B6', '#38BDF8'];

// ─── custom legend helpers ────────────────────────────────────────────────────

const useChartLegend = (items) => {
  const [selected, setSelected] = React.useState(() => new Set(items));
  const itemsKey = items.join('||');
  React.useEffect(() => { setSelected(new Set(items)); }, [itemsKey]); // eslint-disable-line
  const toggle = (chartRef, name) => {
    chartRef?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendToggleSelect', name });
    setSelected(prev => { const n = new Set(prev); n.has(name) ? n.delete(name) : n.add(name); return n; });
  };
  const selectAll = (chartRef) => {
    chartRef?.current?.getEchartsInstance?.()?.dispatchAction({ type: 'legendAllSelect' });
    setSelected(new Set(items));
  };
  const resetAll = (chartRef) => {
    const c = chartRef?.current?.getEchartsInstance?.();
    if (c) { c.dispatchAction({ type: 'legendAllSelect' }); c.dispatchAction({ type: 'legendInverseSelect' }); }
    setSelected(new Set());
  };
  return { selected, toggle, selectAll, resetAll };
};

const ChartLegend = ({ items, colors, chartRef, selected, onToggle, onSelectAll, onReset }) => (
  <div style={{ display:'flex', flexWrap:'wrap', alignItems:'center', gap:'4px 10px', padding:'6px 0 0' }}>
    <button onClick={() => onSelectAll(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.accent}`, borderRadius:'4px', color:C.accent, cursor:'pointer', flexShrink:0 }}>
      All
    </button>
    <button onClick={() => onReset(chartRef)}
      style={{ fontSize:'9px', padding:'2px 8px', background:'transparent', border:`1px solid ${C.border}`, borderRadius:'4px', color:C.textMuted, cursor:'pointer', flexShrink:0 }}>
      Reset
    </button>
    {items.map((name, i) => {
      const isSel = selected.has(name);
      return (
        <div key={name} onClick={() => onToggle(chartRef, name)}
          style={{ display:'flex', alignItems:'center', gap:'3px', cursor:'pointer', opacity: isSel ? 1 : 0.3, flexShrink:0 }}>
          <div style={{ width:'7px', height:'7px', borderRadius:'50%', background: colors[i % colors.length] }}/>
          <span style={{ fontSize:'9px', color: C.textSec }}>{name}</span>
        </div>
      );
    })}
  </div>
);

const QUICK = [
  { id: 'yesterday',       label: 'Yesterday'        },
  { id: 'this_year',       label: 'This Year'         },
  { id: 'this_month',      label: 'This Month'        },
  { id: 'this_week',       label: 'This Week'         },
  { id: 'last_year',       label: 'Last Year'         },
  { id: 'last_month',      label: 'Last Month'        },
  { id: 'last_week',       label: 'Last Week'         },
  { id: 'last_year_month', label: "Last Year's Month" },
  { id: 'custom',          label: 'Custom'            },
  { id: 'all',             label: 'All Time'          },
];

// ─── sub-components ───────────────────────────────────────────────────────────

const KPICard = ({ title, value, icon, iconBg, sub }) => (
  <div style={kpiCard}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'5px' }}>
      <div style={kpiIcon(iconBg)}>{icon}</div>
    </div>
    <p style={{ fontSize:'11px', color:C.textSec, margin:'0 0 4px', fontWeight:'500', textTransform:'uppercase', letterSpacing:'0.5px' }}>{title}</p>
    <p style={{ fontSize:'16px', fontWeight:'700', color:C.textPrimary, margin:0, lineHeight:1.2 }}>{value}</p>
    {sub && <p style={{ fontSize:'10px', color:C.textMuted, margin:'3px 0 0' }}>{sub}</p>}
  </div>
);

const CollapsibleSection = ({ visKey, title, collapsed, onToggle, headerRight, children }) => (
  <div style={{ ...card, marginBottom:'12px' }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: collapsed ? 0 : '8px' }}>
      <p style={{ ...sectionTitle, marginBottom:0 }}>{title}</p>
      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
        {!collapsed && headerRight}
        <button
          onClick={() => onToggle(visKey)}
          style={{ fontSize:'10px', color:C.textSec, background:'none', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'3px 8px', cursor:'pointer' }}
        >
          {collapsed ? 'Expand' : 'Collapse'}
        </button>
      </div>
    </div>
    {!collapsed && children}
  </div>
);

// ─── MultiSelect filter dropdown ──────────────────────────────────────────────
const MultiSelect = ({ label, options, selected, onChange }) => {
  const [open, setOpen]     = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef(null);

  React.useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const toggle = (val) => {
    const next = selected.includes(val) ? selected.filter(x => x !== val) : [...selected, val];
    onChange(next);
  };

  const filtered = (options || []).filter(opt => {
    const lbl = typeof opt === 'object' ? (opt.label || opt.name || '') : (opt || '');
    return String(lbl).toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div ref={ref} style={{ position:'relative', flex:1, minWidth:'130px' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width:'100%', padding:'4px 10px', background:C.bgSurface, border:`1px solid ${selected.length ? C.accent : C.border}`, borderRadius:'8px', fontSize:'11px', color: selected.length ? C.accent : C.textMuted, textAlign:'left', cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}
      >
        <span>{label}{selected.length ? ` (${selected.length})` : ''}</span>
        <span style={{ fontSize:'9px' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:300, background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', marginTop:'4px', boxShadow:'0 4px 12px rgba(0,0,0,0.4)' }}>
          <div style={{ padding:'6px 8px', borderBottom:`1px solid ${C.border}` }}>
            <input
              autoFocus value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..." onClick={e => e.stopPropagation()}
              style={{ width:'100%', padding:'5px 8px', background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'6px', fontSize:'11px', color:C.textPrimary, outline:'none', boxSizing:'border-box' }}
            />
          </div>
          <div style={{ maxHeight:'150px', overflowY:'auto' }}>
            {filtered.length === 0 && <p style={{ padding:'8px 10px', fontSize:'11px', color:C.textMuted }}>No results</p>}
            {filtered.map((opt, i) => {
              const val = typeof opt === 'object' ? (opt.key || opt.code || String(opt)) : String(opt || '');
              const lbl = typeof opt === 'object' ? (opt.label || opt.name || val) : String(opt || '');
              const isSel = selected.includes(val);
              return (
                <div key={i} onClick={() => toggle(val)} style={{ padding:'7px 10px', fontSize:'11px', color: isSel ? C.accent : C.textSec, background: isSel ? C.accentDim : 'transparent', cursor:'pointer', display:'flex', alignItems:'center', gap:'6px' }}>
                  <span style={{ width:'12px', height:'12px', border:`1px solid ${isSel ? C.accent : C.border}`, borderRadius:'3px', background: isSel ? C.accent : 'transparent', display:'inline-flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {isSel && <span style={{ color:'#000', fontSize:'8px' }}>✓</span>}
                  </span>
                  {lbl}
                </div>
              );
            })}
          </div>
          {selected.length > 0 && (
            <div style={{ padding:'6px 8px', borderTop:`1px solid ${C.border}` }}>
              <button onClick={() => { onChange([]); setOpen(false); }}
                style={{ fontSize:'10px', color:C.danger, background:'none', border:'none', cursor:'pointer' }}>Clear All</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── sortable table helpers ───────────────────────────────────────────────────

const SortTh = ({ col, label, sortCol, sortDir, onSort, style }) => (
  <th onClick={() => onSort(col)}
    style={{ ...style, cursor:'pointer', userSelect:'none', whiteSpace:'nowrap' }}>
    {label}
    <span style={{ marginLeft:'4px', opacity: sortCol === col ? 1 : 0.3, fontSize:'9px' }}>
      {sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
    </span>
  </th>
);

const useSortedData = (data, defaultCol, defaultDir = 'desc') => {
  const [sortCol, setSortCol] = React.useState(defaultCol);
  const [sortDir, setSortDir] = React.useState(defaultDir);
  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };
  const sorted = [...data].sort((a, b) => {
    const av = a[sortCol], bv = b[sortCol];
    const an = parseFloat(av), bn = parseFloat(bv);
    const cmp = !isNaN(an) && !isNaN(bn) ? an - bn : String(av || '').localeCompare(String(bv || ''));
    return sortDir === 'asc' ? cmp : -cmp;
  });
  return { sorted, sortCol, sortDir, toggleSort };
};

// ─── main component ───────────────────────────────────────────────────────────

const PaymentAnalysis = () => {
  const [activePeriod, setAP]           = useState(() => localStorage.getItem('ccs_period') || 'this_month');
  const setPeriod = (p) => { localStorage.setItem('ccs_period', p); setAP(p); };
  // ── Branch filter (boss + area_manager only, persists in localStorage) ────
  const [branches, setBranches] = useState([]);
  const _initBranches = () => { try { return JSON.parse(localStorage.getItem('ccs_branch') || '[]'); } catch { return []; } };
  const [selectedBranches, setSelected] = useState(_initBranches);
  const [pendingBranches,  setPending]  = useState(_initBranches);
  const [showBranchDrop, setShowBranchDrop] = useState(false);
  const openBranchDrop  = () => { if (showBranchDrop) { setShowBranchDrop(false); } else { setPending(selectedBranches); setShowBranchDrop(true); } };
  const toggleBranch    = (code) => setPending(prev => prev.includes(code) ? prev.filter(x => x !== code) : [...prev, code]);
  const applyBranches   = () => { localStorage.setItem('ccs_branch', JSON.stringify(pendingBranches)); setSelected(pendingBranches); setShowBranchDrop(false); };
  const resetBranches   = () => { setPending([]); setSelected([]); localStorage.setItem('ccs_branch', '[]'); setShowBranchDrop(false); };

  // Load available branches for this tenant
  useEffect(() => {
    import('../../services/api').then(({ default: api }) => {
      api.get('/api/branches').then(r => setBranches(r.data.branches || [])).catch(() => {});
    });
  }, []);

    // Compute date range synchronously from period string
  const _computeDates = (period) => {
    const now = new Date();
    const _p  = n => String(n).padStart(2, '0');
    const _f  = d => `${d.getFullYear()}-${_p(d.getMonth()+1)}-${_p(d.getDate())}`;
    if (period === 'yesterday')      { const y = new Date(now); y.setDate(y.getDate()-1); return [_f(y), _f(y)]; }
    if (period === 'this_month')     return [_f(new Date(now.getFullYear(), now.getMonth(), 1)), _f(now)];
    if (period === 'this_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day+1); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'this_year')      return [_f(new Date(now.getFullYear(), 0, 1)), _f(now)];
    if (period === 'last_year')      return [_f(new Date(now.getFullYear()-1, 0, 1)), _f(new Date(now.getFullYear()-1, 11, 31))];
    if (period === 'last_month')     return [_f(new Date(now.getFullYear(), now.getMonth()-1, 1)), _f(new Date(now.getFullYear(), now.getMonth(), 0))];
    if (period === 'last_week')      { const d = new Date(now); const day = d.getDay()||7; d.setDate(d.getDate()-day-6); const e = new Date(d); e.setDate(d.getDate()+6); return [_f(d), _f(e)]; }
    if (period === 'last_year_month'){ const m = now.getMonth(), ly = now.getFullYear()-1; return [_f(new Date(ly,m,1)), _f(new Date(ly,m+1,0))]; }
    if (period === 'custom')        return [localStorage.getItem('ccs_from')||'', localStorage.getItem('ccs_to')||''];
    return ['', ''];
  };
  const _initPeriod = localStorage.getItem('ccs_period') || 'this_month';
  const [_initFrom, _initTo] = _computeDates(_initPeriod);
  const _initPendFrom = _initPeriod === 'custom' ? (localStorage.getItem('ccs_from')||'') : '';
  const _initPendTo   = _initPeriod === 'custom' ? (localStorage.getItem('ccs_to')||'')   : '';
const [dateFrom, setDateFrom]         = useState(_initFrom);
  const [dateTo,   setDateTo]           = useState(_initTo);
  const [pendingFrom, setPF]            = useState(_initPendFrom);
  const [pendingTo,   setPT]            = useState(_initPendTo);

  const [byMode, setByMode]             = useState([]);
  const [trend, setTrend]               = useState([]);
  const [totalTrend, setTotalTrend]     = useState([]);
  const [totals, setTotals]             = useState(null);
  const [loading, setLoading]           = useState(true);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendPeriod, setTrendPeriod]   = useState(() => localStorage.getItem('ccs_gran') || 'daily');
  const setGran = (g) => { localStorage.setItem('ccs_gran', g); setTrendPeriod(g); };

  // Force monthly for All Time to prevent massive daily datasets
  useEffect(() => {
    if (activePeriod === 'all' && trendPeriod !== 'monthly') setGran('monthly');
  }, [activePeriod]);
  const [collapsed, setCollapsed]       = useState({});

  // Global filter — Payment Mode only, persisted so it carries forward across tabs
  const _initFilters = () => { try { return JSON.parse(localStorage.getItem('ccs_filters') || '{}'); } catch { return {}; } };
  const _pf = _initFilters();
  const [filterOpts,     setFilterOpts]    = useState({ payment_modes:[] });
  const [filtersOpen,    setFO]            = useState(false);
  const [activeFilters,  setAF]            = useState({ payment_mode: _pf.payment_mode || [] });
  const [pendingFilters, setPF_]           = useState({ payment_mode: _pf.payment_mode || [] });
  // applyFilters merges into ccs_filters so other tabs' filters (staff/classify/etc) are preserved
  const applyFilters = (f) => {
    const existing = _initFilters();
    localStorage.setItem('ccs_filters', JSON.stringify({ ...existing, ...f }));
    setAF(f);
  };
  const activeFiltersCount = activeFilters.payment_mode.length;

  // Track previous trend period to distinguish period-change from date-change
  const prevTrendPeriod = useRef(trendPeriod);
  const multiLineRef = useRef(null);
  const donutRef     = useRef(null);

  useEffect(() => {
    getVisualPrefs().then(r => setCollapsed(r.data || {})).catch(() => {});
  }, []);

  useEffect(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    if (selectedBranches.length) p.branch_code = selectedBranches.join(',');
    getPaymentFilterOptions(p).then(r => setFilterOpts(r.data || {})).catch(() => {});
  }, [dateFrom, dateTo, selectedBranches]);

  // Quick filter → dates
  useEffect(() => {
    if (activePeriod === 'custom') return;
    const now = new Date();
    const _p = n => String(n).padStart(2, '0');
    const _f = d => `${d.getFullYear()}-${_p(d.getMonth()+1)}-${_p(d.getDate())}`;
    let from = '', to = '';
    if (activePeriod === 'yesterday') {
      const y = new Date(now); y.setDate(y.getDate() - 1);
      from = to = _f(y);
    } else if (activePeriod === 'this_month') {
      from = _f(new Date(now.getFullYear(), now.getMonth(), 1));
      to   = _f(now);
    } else if (activePeriod === 'this_week') {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day + 1);
      const e = new Date(d); e.setDate(d.getDate() + 6);
      from = _f(d);
      to   = _f(e);
    } else if (activePeriod === 'this_year') {
      from = _f(new Date(now.getFullYear(), 0, 1));
      to   = _f(now);
    } else if (activePeriod === 'last_year') {
      from = _f(new Date(now.getFullYear() - 1, 0, 1));
      to   = _f(new Date(now.getFullYear() - 1, 11, 31));
    } else if (activePeriod === 'last_month') {
      from = _f(new Date(now.getFullYear(), now.getMonth() - 1, 1));
      to   = _f(new Date(now.getFullYear(), now.getMonth(), 0));
    } else if (activePeriod === 'last_week') {
      const d = new Date(now);
      const day = d.getDay() || 7;
      d.setDate(d.getDate() - day - 6); // last Monday
      const e = new Date(d); e.setDate(d.getDate() + 6); // last Sunday
      from = _f(d);
      to   = _f(e);
    } else if (activePeriod === 'last_year_month') {
      const m = now.getMonth();
      const ly = now.getFullYear() - 1;
      from = _f(new Date(ly, m, 1));
      to   = _f(new Date(ly, m + 1, 0));
    }
    setDateFrom(from);
    setDateTo(to);
  }, [activePeriod]);

  // Full reload when dates, branches, or filters change
  useEffect(() => { loadAll(); }, [dateFrom, dateTo, selectedBranches, activeFilters]);

  // Lightweight trend-only reload when period granularity changes (no full spinner)
  useEffect(() => {
    if (prevTrendPeriod.current === trendPeriod) return;
    prevTrendPeriod.current = trendPeriod;
    loadTrendsOnly();
  }, [trendPeriod]);

  const dateParams = useCallback(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo)   p.date_to   = dateTo;
    if (selectedBranches.length > 0) p.branch_code = selectedBranches.join(',');
    if (activeFilters.payment_mode.length) p.payment_mode = activeFilters.payment_mode.join(',');
    return p;
  }, [dateFrom, dateTo, selectedBranches, activeFilters]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await getPaymentAnalysis({ ...dateParams(), period: trendPeriod });
      setByMode(res.data.by_mode || []);
      setTrend(res.data.trend || []);
      setTotalTrend(res.data.total_trend || []);
      setTotals({
        total:           res.data.total_collected,
        txns:            res.data.total_transactions,
        total_bills_C:   res.data.total_bills_c   ?? null,
        paid_bills_H:    res.data.paid_bills_h    ?? null,
        avg_per_paid_bill: res.data.avg_per_paid_bill ?? null,
      });
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadTrendsOnly = async () => {
    setTrendLoading(true);
    try {
      const res = await getPaymentAnalysis({ ...dateParams(), period: trendPeriod });
      setTrend(res.data.trend || []);
      setTotalTrend(res.data.total_trend || []);
    } catch(e) { console.error(e); }
    finally { setTrendLoading(false); }
  };

  const applyCustom = () => {
    localStorage.setItem('ccs_from', pendingFrom);
    localStorage.setItem('ccs_to', pendingTo);
    setDateFrom(pendingFrom);
    setDateTo(pendingTo);
  };

  const toggleCollapse = useCallback((key) => {
    const next = !collapsed[key];
    setCollapsed(prev => ({ ...prev, [key]: next }));
    setVisualPref({ visual_key: key, is_collapsed: next }).catch(() => {});
  }, [collapsed]);

  // ── derived ───────────────────────────────────────────────────────────────

  const totalCollected    = totals?.total || 0;
  const totalTxns         = totals?.txns  || 0;
  const avgTxnValue       = totalTxns > 0 ? totalCollected / totalTxns : 0;
  const topMode           = byMode.length > 0 ? byMode[0] : null;
  const totalBillsC       = totals?.total_bills_C    ?? null;
  const paidBillsH        = totals?.paid_bills_H     ?? null;
  const avgPerPaidBill    = totals?.avg_per_paid_bill ?? null;

  // ── table sort state (must be at component level — Rules of Hooks) ──────────
  const byModeWithPct = byMode.map(m => ({ ...m, pct: totalCollected > 0 ? (parseFloat(m.revenue) / totalCollected) * 100 : 0 }));
  const { sorted: modeSorted, sortCol: mCol, sortDir: mDir, toggleSort: mSort } = useSortedData(byModeWithPct, 'revenue');

  const modes           = [...new Set(trend.map(t => t.payment_mode))];
  const multiLineLegend = useChartLegend(modes);
  const donutModes      = byMode.map(m => m.payment_mode || 'Unknown');
  const donutLegend     = useChartLegend(donutModes);
  const totalPeriodKeys = generateFilledPeriodKeys(
    sortPeriodKeys([...new Set(totalTrend.map(t => t.period))]),
    trendPeriod
  );
  const trendPeriodKeys = generateFilledPeriodKeys(
    sortPeriodKeys([...new Set(trend.map(t => t.period))]),
    trendPeriod
  );

  // Pre-build O(1) lookup: "isoKey||payment_mode" → trend row
  const trendIndex = {};
  trend.forEach(t => { trendIndex[`${toISOKey(t.period)}||${t.payment_mode}`] = t; });

  const multiYearTotal  = isMultiYear(totalPeriodKeys);
  const multiYearMode   = isMultiYear(trendPeriodKeys);

  const totalMarkArea   = buildMarkArea(totalPeriodKeys);
  const modeMarkArea    = buildMarkArea(trendPeriodKeys);

  // ── chart options ─────────────────────────────────────────────────────────

  const totalTrendOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: C.bgElevated, borderColor: C.border,
      textStyle: { color: C.textPrimary, fontSize: 11 },
      formatter: (params) => {
        const lbl = fmtDate(params[0]?.name, trendPeriod === 'monthly'
          ? { month:'short', year:'numeric' }
          : { day:'numeric', month:'short', year: multiYearTotal ? 'numeric' : undefined });
        return `${lbl}<br/><b>RM ${Number(params[0]?.value||0).toLocaleString(undefined,{maximumFractionDigits:0})}</b>`;
      },
    },
    grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
    xAxis: {
      type:'category', data: totalPeriodKeys,
      axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false },
      axisLabel: { color:C.textMuted, fontSize:9, rotate: multiYearTotal ? 30 : 0, formatter: makeLabelFmt(trendPeriod, multiYearTotal) },
    },
    yAxis: {
      type:'value', splitLine: { lineStyle: { color: C.bgElevated } },
      axisLabel: { color:C.textMuted, fontSize:10, formatter: v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v },
    },
    series: [{
      type:'line', smooth:true, symbol:'circle',
      symbolSize: totalPeriodKeys.length > 60 ? 2 : 4,
      showSymbol: totalPeriodKeys.length <= 5,
      data: (() => { const m = {}; totalTrend.forEach(t => { m[toISOKey(t.period)] = parseFloat(t.revenue || 0); }); return totalPeriodKeys.map(k => m[k] ?? 0); })(),
      lineStyle: { color: C.accent, width: 2.5 }, itemStyle: { color: C.accent },
      areaStyle: { color: { type:'linear', x:0, y:0, x2:0, y2:1, colorStops:[{ offset:0, color:'rgba(168,224,99,0.2)' },{ offset:1, color:'rgba(168,224,99,0)' }] } },
      markArea: totalMarkArea.length ? { silent:true, data: totalMarkArea } : undefined,
    }]
  };

  const multiLineOption = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger:'axis', backgroundColor: C.bgElevated, borderColor: C.border,
      textStyle: { color: C.textPrimary, fontSize: 11 },
      formatter: (params) => {
        const lbl = fmtDate(params[0]?.name, trendPeriod === 'monthly'
          ? { month:'short', year:'numeric' }
          : { day:'numeric', month:'short', year: multiYearMode ? 'numeric' : undefined });
        return lbl + '<br/>' + params.map(p =>
          `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>RM ${Number(p.value||0).toLocaleString(undefined,{maximumFractionDigits:0})}</b>`
        ).join('<br/>');
      },
    },
    legend: { show: false },
    grid: { left:8, right:8, top:8, bottom:8, containLabel:true },
    xAxis: {
      type:'category', data: trendPeriodKeys,
      axisLine: { lineStyle: { color: C.border } }, axisTick: { show: false },
      axisLabel: { color:C.textMuted, fontSize:9, rotate: multiYearMode ? 30 : 0, formatter: makeLabelFmt(trendPeriod, multiYearMode), hideOverlap: true },
    },
    yAxis: {
      type:'value', splitLine: { lineStyle: { color: C.bgElevated } },
      axisLabel: { color:C.textMuted, fontSize:10, formatter: v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v },
    },
    series: modes.map((mode, i) => ({
      name: mode, type:'line', smooth:true, symbol:'circle',
      symbolSize: trendPeriodKeys.length > 60 ? 2 : 3,
      showSymbol: trendPeriodKeys.length <= 5,
      data: trendPeriodKeys.map(pk => {
        const found = trendIndex[`${pk}||${mode}`];
        return found ? parseFloat(found.revenue || 0) : 0;
      }),
      lineStyle: { color: MODE_COLORS[i % MODE_COLORS.length], width: 2 },
      itemStyle: { color: MODE_COLORS[i % MODE_COLORS.length] },
      markArea: i === 0 && modeMarkArea.length ? { silent:true, data: modeMarkArea } : undefined,
    }))
  };

  const donutOption = {
    backgroundColor: 'transparent',
    tooltip: { trigger:'item', backgroundColor: C.bgElevated, borderColor: C.border, textStyle: { color: C.textPrimary, fontSize: 11 }, formatter: p => `${p.name}<br/>RM ${Number(p.value).toLocaleString(undefined,{maximumFractionDigits:0})}<br/>${p.percent}%` },
    legend: { show: false },
    series: [{
      type:'pie', radius:['38%','65%'], center:['50%','50%'],
      data: byMode.map((m, i) => ({ name: m.payment_mode || 'Unknown', value: parseFloat(m.revenue || 0), itemStyle: { color: MODE_COLORS[i % MODE_COLORS.length] } })),
      label: { show: false },
    }]
  };

  const periodToggle = (
    <div style={toggleGroup}>
      {['daily','weekly','monthly'].map(p => {
        const disabled = activePeriod === 'all' && p !== 'monthly';
        return (
          <button key={p} style={{ ...toggleBtn(trendPeriod === p), opacity: disabled ? 0.4 : 1, cursor: disabled ? 'default' : 'pointer' }}
            onClick={() => !disabled && setGran(p)}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        );
      })}
    </div>
  );

  // Tiny spinner shown inside trend cards while updating granularity (no full-page flash)
  const RefreshBadge = () => trendLoading ? (
    <div style={{ display:'flex', alignItems:'center', gap:'4px', marginBottom:'4px' }}>
      <div style={{ width:'10px', height:'10px', border:`2px solid ${C.bgElevated}`, borderTopColor:C.accent, borderRadius:'50%', animation:'spin 0.8s linear infinite' }}/>
      <span style={{ fontSize:'10px', color:C.textMuted }}>Updating…</span>
    </div>
  ) : null;

  if (loading) return (
    <div style={{ display:'flex', justifyContent:'center', alignItems:'center', height:'60vh' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:'40px', height:'40px', border:`3px solid ${C.bgElevated}`, borderTopColor:C.amber, borderRadius:'50%', animation:'spin 0.8s linear infinite', margin:'0 auto 12px' }}/>
        <p style={{ color:C.textMuted, fontSize:'13px' }}>Loading payment data...</p>
      </div>
    </div>
  );

  return (
    <div style={{ padding:'16px', paddingBottom:'32px' }}>

      <div style={{ display:'flex', gap:'8px', marginBottom:'12px', flexWrap:'wrap' }}>
        {QUICK.map(q => <button key={q.id} onClick={() => setPeriod(q.id)} style={filterPill(activePeriod === q.id)}>{q.label}</button>)}
      </div>
          {/* Branch multi-select — boss, area_manager, superadmin only */}
          {(() => {
            try {
              const u = JSON.parse(localStorage.getItem('user') || '{}');
              if (!['boss','area_manager','superadmin'].includes(u.role)) return null;
            } catch { return null; }
            if (branches.length === 0) return null;
            const label = selectedBranches.length === 0 ? 'All Branches'
              : selectedBranches.length === 1 ? (branches.find(b => b.code === selectedBranches[0])?.name || selectedBranches[0])
              : `${selectedBranches.length} Branches`;
            return (
              <div style={{ marginBottom:'10px', position:'relative', display:'inline-block' }}>
                <button onClick={openBranchDrop}
                  style={{ ...filterPill(selectedBranches.length > 0), display:'flex', alignItems:'center', gap:'6px', padding:'6px 14px' }}>
                  🏢 {label} <span style={{ fontSize:'10px', opacity:0.7 }}>{showBranchDrop ? '▴' : '▾'}</span>
                </button>
                {showBranchDrop && (
                  <div style={{ position:'absolute', top:'38px', left:0, zIndex:999,
                    background:C.bgElevated, border:`1px solid ${C.border}`, borderRadius:'12px',
                    padding:'8px', minWidth:'180px', boxShadow:'0 8px 32px rgba(0,0,0,0.4)' }}>
                    {branches.map(b => (
                      <label key={b.code} style={{ display:'flex', alignItems:'center', gap:'8px',
                        padding:'7px 10px', cursor:'pointer', borderRadius:'8px', fontSize:'13px',
                        color: pendingBranches.includes(b.code) ? C.accent : C.textSec,
                        background: pendingBranches.includes(b.code) ? 'rgba(168,224,99,0.1)' : 'transparent' }}>
                        <input type="checkbox" checked={pendingBranches.includes(b.code)}
                          onChange={() => toggleBranch(b.code)} style={{ accentColor: C.accent }} />
                        {b.name || b.code}
                      </label>
                    ))}
                    <div style={{ borderTop:`1px solid ${C.border}`, marginTop:'6px', paddingTop:'6px', display:'flex', flexDirection:'column', gap:'4px' }}>
                      <button onClick={applyBranches}
                        style={{ width:'100%', background:C.accent, border:'none', borderRadius:'6px',
                          color:'#000', fontSize:'12px', fontWeight:'700', cursor:'pointer', padding:'6px 0' }}>
                        Apply
                      </button>
                      <button onClick={resetBranches}
                        style={{ width:'100%', background:'transparent', border:'none',
                          color:C.textMuted, fontSize:'12px', cursor:'pointer', padding:'4px 0' }}>
                        ↩ Reset to All
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

      {activePeriod === 'custom' && (
        <div style={{ display:'flex', gap:'8px', marginBottom:'12px', alignItems:'flex-end' }}>
          {[['From', pendingFrom, setPF], ['To', pendingTo, setPT]].map(([label, val, setter]) => (
            <div key={label} style={{ flex:1 }}>
              <p style={{ fontSize:'10px', color:C.textMuted, marginBottom:'4px' }}>{label}</p>
              <input type="date" value={val} onChange={e => setter(e.target.value)}
                style={{ width:'100%', padding:'8px 10px', background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'8px', fontSize:'12px', color: val ? C.textPrimary : C.textMuted, outline:'none', boxSizing:'border-box' }}
              />
            </div>
          ))}
          <button onClick={applyCustom} disabled={!pendingFrom || !pendingTo}
            style={{ padding:'8px 14px', background: pendingFrom && pendingTo ? C.accent : C.bgElevated, color: pendingFrom && pendingTo ? '#000' : C.textMuted, border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'600', cursor: pendingFrom && pendingTo ? 'pointer' : 'default', flexShrink:0 }}>
            Apply
          </button>
        </div>
      )}

      {/* ── PAYMENT MODE FILTER ── */}
      <div style={{ marginBottom:'12px' }}>
        <button onClick={() => setFO(o => !o)} style={{ ...filterPill(activeFiltersCount > 0), display:'flex', alignItems:'center', gap:'6px' }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M3 6h18M7 12h10M11 18h2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          Payment Mode{activeFiltersCount > 0 ? ` (${activeFiltersCount})` : ''}
          <span style={{ fontSize:'9px' }}>{filtersOpen ? '▲' : '▼'}</span>
        </button>
        {filtersOpen && (
          <div style={{ marginTop:'8px', padding:'12px', background:C.bgSurface, border:`1px solid ${C.border}`, borderRadius:'12px' }}>
            <div style={{ display:'flex', flexWrap:'wrap', gap:'8px', alignItems:'flex-start' }}>
              <MultiSelect
                label="Payment Mode"
                options={(filterOpts.payment_modes || [])}
                selected={pendingFilters.payment_mode}
                onChange={v => setPF_(f => ({ ...f, payment_mode: v }))}
              />
              <button
                onClick={() => { const empty = { payment_mode:[] }; setPF_(empty); applyFilters(empty); }}
                style={{ alignSelf:'flex-end', fontSize:'11px', color:C.danger, background:'none', border:`1px solid ${C.border}`, borderRadius:'6px', padding:'5px 10px', cursor:'pointer', whiteSpace:'nowrap' }}
              >
                Reset
              </button>
            </div>
            <div style={{ marginTop:'10px', display:'flex', justifyContent:'flex-end' }}>
              <button
                onClick={() => { applyFilters({ payment_mode: pendingFilters.payment_mode }); setFO(false); }}
                style={{ padding:'7px 20px', background:C.accent, color:'#000', border:'none', borderRadius:'8px', fontSize:'12px', fontWeight:'700', cursor:'pointer' }}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      <CollapsibleSection visKey="pa_kpi" title="Summary" collapsed={!!collapsed['pa_kpi']} onToggle={toggleCollapse}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
          <KPICard title="Total Collection" value={`RM ${(totalCollected||0).toLocaleString(undefined,{maximumFractionDigits:0})}`}           iconBg={C.accentDim} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 100 7h5a3.5 3.5 0 110 7H6" stroke={C.accent} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Total Bills"       value={totalBillsC !== null ? totalBillsC.toLocaleString() : '–'} iconBg={C.blueDim}   icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 5h6" stroke={C.blue} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Paid Bills"        value={paidBillsH !== null ? paidBillsH.toLocaleString() : '–'}   iconBg={C.amberDim}  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" stroke={C.amber} strokeWidth="2" strokeLinecap="round"/></svg>} />
          <KPICard title="Avg / Paid Bill" value={avgPerPaidBill !== null ? `RM ${Number(avgPerPaidBill).toLocaleString(undefined,{maximumFractionDigits:0})}` : '–'} iconBg={C.purpleDim} icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" stroke={C.purple} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>} />
        </div>
      </CollapsibleSection>

      <CollapsibleSection visKey="pa_total_trend" title="Total Collection Trend" collapsed={!!collapsed['pa_total_trend']} onToggle={toggleCollapse} headerRight={periodToggle}>
        <RefreshBadge />
        <ReactECharts option={totalTrendOption} style={{ height:'200px' }} />
      </CollapsibleSection>

      {modes.length > 0 && (
        <CollapsibleSection visKey="pa_mode_trend" title="Mode Trend" collapsed={!!collapsed['pa_mode_trend']} onToggle={toggleCollapse} headerRight={periodToggle}>
          <RefreshBadge />
          <ReactECharts ref={multiLineRef} option={multiLineOption} style={{ height:'240px', width:'100%' }} />
          <ChartLegend
            items={modes} colors={MODE_COLORS} chartRef={multiLineRef}
            selected={multiLineLegend.selected}
            onToggle={multiLineLegend.toggle}
            onSelectAll={multiLineLegend.selectAll}
            onReset={multiLineLegend.resetAll}
          />
        </CollapsibleSection>
      )}

      <CollapsibleSection visKey="pa_donut" title="Revenue by Payment Mode" collapsed={!!collapsed['pa_donut']} onToggle={toggleCollapse}>
        <ReactECharts ref={donutRef} option={donutOption} style={{ height:'220px', width:'100%' }} />
        <ChartLegend
          items={donutModes} colors={MODE_COLORS} chartRef={donutRef}
          selected={donutLegend.selected}
          onToggle={donutLegend.toggle}
          onSelectAll={donutLegend.selectAll}
          onReset={donutLegend.resetAll}
        />
      </CollapsibleSection>

      <CollapsibleSection visKey="pa_table" title="Payment Summary" collapsed={!!collapsed['pa_table']} onToggle={toggleCollapse}>
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'12px' }}>
            <thead>
              <tr>
                <SortTh col="payment_mode" label="Mode"         sortCol={mCol} sortDir={mDir} onSort={mSort} style={th} />
                <SortTh col="revenue"       label="Revenue"      sortCol={mCol} sortDir={mDir} onSort={mSort} style={th} />
                <SortTh col="transactions"  label="Transactions" sortCol={mCol} sortDir={mDir} onSort={mSort} style={th} />
                <SortTh col="pct"           label="% of Total"   sortCol={mCol} sortDir={mDir} onSort={mSort} style={th} />
              </tr>
            </thead>
            <tbody>
              {modeSorted.map((m, i) => (
                <tr key={i}>
                  <td style={{ ...td, color:C.textPrimary }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <div style={{ width:'8px', height:'8px', borderRadius:'50%', background: MODE_COLORS[byMode.findIndex(x => x.payment_mode === m.payment_mode) % MODE_COLORS.length], flexShrink:0 }}/>
                      {m.payment_mode || 'Unknown'}
                    </div>
                  </td>
                  <td style={tdR}>RM {parseFloat(m.revenue || 0).toLocaleString(undefined, { maximumFractionDigits:0 })}</td>
                  <td style={tdR}>{m.transactions}</td>
                  <td style={tdR}>{m.pct.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default PaymentAnalysis;